import { useCallback, useEffect, useRef, useState } from 'react';
import { I18n } from '@/i18n/I18n';
import { ReportBundleFiles } from '@/reports/bundles/types';
import { AssistantContentBlock, AssistantMessage, AssistantReply, AssistantStreamEvent } from '@/types/reportAssistantTypes';
import { AssistantWorkspace, ToolDisplay, pendingDisplay, runAssistantTool } from './assistantTools';
import { AssistantTransport, storeAssistantTransport } from './transport';

/**
 * The assistant's loop, run in the studio: send the history, stream the
 * reply, run whatever tools it calls against the workspace, send their
 * results, until the model ends its turn or the author stops it. The history
 * is the Messages API's own, kept here whole, and saved per bundle in this
 * browser so a reload doesn't lose the conversation.
 */

export interface ToolState extends ToolDisplay {
  status: 'running' | 'done' | 'error';
  result?: string;
}

export interface AssistantNotice {
  /** shown after this many messages of the history */
  after: number;
  text: string;
  tone: 'info' | 'warn' | 'error';
}

/** A block of the reply being written, as it streams. */
export interface LiveBlock {
  kind: 'text' | 'thinking' | 'tool_use';
  text: string;
  name?: string;
}

interface Saved {
  history: AssistantMessage[];
  tools: Record<string, ToolState>;
  /** the files as they were before each of the author's messages, by its index in the history */
  checkpoints: Record<number, ReportBundleFiles>;
  notices: AssistantNotice[];
}

export interface BundleAssistant extends Saved {
  running: boolean;
  live: LiveBlock[] | null;
  send: (prompt: string) => void;
  stop: () => void;
  reset: () => void;
  /** put the files back as they were before the author's message at `index` */
  restore: (index: number) => void;
}

const MAX_STEPS = 60;
const STUDIO_NOTE = '<studio>';
const storageKey = (bundleId: string) => `veriform.reportAssistant.${bundleId}`;
const EMPTY: Saved = { history: [], tools: {}, checkpoints: {}, notices: [] };

/** Is this block the studio's note rather than something the author typed? */
export const isStudioNote = (block: AssistantContentBlock) => block.type === 'text' && block.text.startsWith(STUDIO_NOTE);

function load(bundleId: string): Saved {
  try {
    const raw = window.localStorage.getItem(storageKey(bundleId));
    return raw ? { ...EMPTY, ...(JSON.parse(raw) as Saved) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

function save(bundleId: string, saved: Saved): void {
  try {
    if (saved.history.length === 0) window.localStorage.removeItem(storageKey(bundleId));
    else window.localStorage.setItem(storageKey(bundleId), JSON.stringify(saved));
  } catch {
    // a full or blocked storage only costs the conversation surviving a reload
  }
}

const changedPaths = (a: ReportBundleFiles, b: ReportBundleFiles) => Object.keys({ ...a, ...b }).filter((p) => a[p] !== b[p]).sort();

export interface StudioState {
  /** a schema's own page rather than a report bundle's */
  subject?: 'bundle' | 'schema';
  /** a schema's: the reports that read it */
  readers?: string[];
  bundleKey: string;
  bundleTitle: string;
  activeFile: string;
  previewRun: string;
  compiles: boolean;
  problems: number;
  dirtyFiles: string[];
}

/** The note each of the author's messages opens with: where the studio stands right now. */
function studioNote(state: StudioState, since: string[]): string {
  if (state.subject === 'schema') {
    return [
      STUDIO_NOTE,
      `Schema: ${state.bundleKey} — "${state.bundleTitle}"`,
      `Draft: ${state.compiles ? 'a sheet' : `not a sheet (${state.problems} problem${state.problems === 1 ? '' : 's'})`}${state.dirtyFiles.length ? '; unsaved changes in inputs.json' : ''}`,
      `Read by: ${state.readers?.length ? state.readers.join(', ') : 'no report yet'}`,
      ...since,
      '</studio>',
    ].join('\n');
  }
  const lines = [
    STUDIO_NOTE,
    `Bundle: ${state.bundleKey} — "${state.bundleTitle}"`,
    `Open in the editor: ${state.activeFile}`,
    `Preview: ${state.previewRun}`,
    `Draft: ${state.compiles ? 'compiles' : `does not compile (${state.problems} problem${state.problems === 1 ? '' : 's'})`}${state.dirtyFiles.length ? `; unsaved changes in ${state.dirtyFiles.join(', ')}` : ''}`,
    ...since,
    '</studio>',
  ];
  return lines.join('\n');
}

export function useBundleAssistant(args: {
  bundleId: string;
  workspace: AssistantWorkspace;
  studioState: () => StudioState;
  transport?: AssistantTransport;
}): BundleAssistant {
  const { bundleId, workspace, studioState } = args;
  const transport = args.transport ?? storeAssistantTransport;
  const [saved, setSaved] = useState<Saved>(() => load(bundleId));
  const [running, setRunning] = useState(false);
  const [live, setLive] = useState<LiveBlock[] | null>(null);
  const savedRef = useRef(saved);
  const abortRef = useRef<AbortController | null>(null);
  /** the files when the assistant last finished, to tell it what the author changed since */
  const filesAtRestRef = useRef<ReportBundleFiles | null>(null);
  const sinceRef = useRef<string[]>([]);

  const update = useCallback((change: (s: Saved) => Saved) => {
    savedRef.current = change(savedRef.current);
    setSaved(savedRef.current);
  }, []);

  useEffect(() => {
    savedRef.current = load(bundleId);
    setSaved(savedRef.current);
  }, [bundleId]);

  useEffect(() => {
    if (!running) save(bundleId, saved);
  }, [bundleId, saved, running]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /** The author's side of the history always ends the history: a new message merges into a trailing one. */
  const pushUser = (content: AssistantContentBlock[]): number => {
    let index = 0;
    update((s) => {
      const last = s.history[s.history.length - 1];
      if (last?.role === 'user') {
        index = s.history.length - 1;
        return { ...s, history: [...s.history.slice(0, -1), { role: 'user', content: [...last.content, ...content] }] };
      }
      index = s.history.length;
      return { ...s, history: [...s.history, { role: 'user', content }] };
    });
    return index;
  };

  const notice = (text: string, tone: AssistantNotice['tone']) => update((s) => ({ ...s, notices: [...s.notices, { after: s.history.length, text, tone }] }));
  const setTool = (id: string, state: ToolState) => update((s) => ({ ...s, tools: { ...s.tools, [id]: state } }));

  const streamReply = async (signal: AbortSignal): Promise<AssistantReply | null> => {
    const blocks: LiveBlock[] = [];
    setLive([]);
    let reply: AssistantReply | null = null;
    let error: string | null = null;
    const onEvent = (event: AssistantStreamEvent) => {
      if (event.type === 'block') blocks[event.index] = { kind: event.kind, text: '', name: event.name };
      else if ((event.type === 'text' || event.type === 'thinking') && blocks[event.index]) blocks[event.index] = { ...blocks[event.index], text: blocks[event.index].text + event.text };
      else if (event.type === 'reply') reply = event.reply;
      else if (event.type === 'error') error = event.message;
      setLive(blocks.filter(Boolean).slice());
    };
    try {
      await transport(bundleId, savedRef.current.history, onEvent, signal);
    } catch (e) {
      if (signal.aborted) return null;
      error = e instanceof Error ? e.message : String(e);
    } finally {
      setLive(null);
    }
    if (signal.aborted) return null;
    if (error || !reply) {
      notice(error ?? I18n.get('reportAssistantNoReply'), 'error');
      return null;
    }
    return reply;
  };

  const loop = async () => {
    const abort = new AbortController();
    abortRef.current = abort;
    setRunning(true);
    try {
      for (let step = 0; step < MAX_STEPS; step += 1) {
        const reply = await streamReply(abort.signal);
        if (!reply) break;
        update((s) => ({ ...s, history: [...s.history, { role: 'assistant', content: reply.content }] }));
        const uses = reply.content.filter((b): b is Extract<AssistantContentBlock, { type: 'tool_use' }> => b.type === 'tool_use');
        if (reply.stopReason === 'tool_use' && uses.length > 0) {
          const results: AssistantContentBlock[] = [];
          for (const use of uses) {
            if (abort.signal.aborted) {
              results.push({ type: 'tool_result', tool_use_id: use.id, content: 'The author stopped the assistant before this ran.', is_error: true });
              setTool(use.id, { ...pendingDisplay(use.name, use.input), status: 'error', result: I18n.get('reportAssistantStopped') });
              continue;
            }
            setTool(use.id, { ...pendingDisplay(use.name, use.input), status: 'running' });
            const outcome = await runAssistantTool(use.name, use.input, workspace);
            setTool(use.id, { ...outcome.display, status: outcome.isError ? 'error' : 'done', result: outcome.content });
            results.push({ type: 'tool_result', tool_use_id: use.id, content: outcome.content, ...(outcome.isError ? { is_error: true } : {}) });
          }
          pushUser(results);
          if (abort.signal.aborted) break;
          continue;
        }
        // a reply that ends any other way and still holds tool calls cannot leave them unanswered
        if (uses.length > 0) pushUser(uses.map((use) => ({ type: 'tool_result' as const, tool_use_id: use.id, content: 'Not run: the reply was cut off before this call was complete.', is_error: true })));
        if (reply.stopReason === 'max_tokens') notice(I18n.get('reportAssistantCutOff'), 'warn');
        else if (reply.stopReason === 'refusal') notice(I18n.get('reportAssistantRefused'), 'warn');
        else if (reply.stopReason === 'model_context_window_exceeded') notice(I18n.get('reportAssistantTooLong'), 'warn');
        break;
      }
      if (abort.signal.aborted) notice(I18n.get('reportAssistantStopped'), 'info');
    } finally {
      abortRef.current = null;
      filesAtRestRef.current = workspace.getFiles();
      setRunning(false);
    }
  };

  const send = (prompt: string) => {
    const text = prompt.trim();
    if (!text || abortRef.current) return;
    const files = workspace.getFiles();
    const since = [...sinceRef.current];
    if (filesAtRestRef.current) {
      const edited = changedPaths(filesAtRestRef.current, files);
      if (edited.length) since.push(`Since your last turn the author changed ${edited.join(', ')}.`);
    }
    sinceRef.current = [];
    const index = pushUser([{ type: 'text', text: studioNote(studioState(), since) }, { type: 'text', text }]);
    update((s) => ({ ...s, checkpoints: { ...s.checkpoints, [index]: s.checkpoints[index] ?? files } }));
    void loop();
  };

  const stop = () => abortRef.current?.abort();

  const reset = () => {
    abortRef.current?.abort();
    filesAtRestRef.current = null;
    sinceRef.current = [];
    update(() => EMPTY);
  };

  const restore = (index: number) => {
    const files = savedRef.current.checkpoints[index];
    if (!files || abortRef.current) return;
    workspace.setFiles(files);
    const message = savedRef.current.history[index];
    const typed = message?.content.find((b) => b.type === 'text' && !isStudioNote(b));
    const quoted = typed && typed.type === 'text' ? `"${typed.text.slice(0, 80)}${typed.text.length > 80 ? '…' : ''}"` : 'an earlier message';
    sinceRef.current.push(`The author put the files back as they were before their message ${quoted}; whatever you changed after it is undone.`);
    filesAtRestRef.current = files;
    notice(I18n.get('reportAssistantRestored'), 'info');
  };

  return { ...saved, running, live, send, stop, reset, restore };
}
